import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldDefaultOptions, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
	MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS,
	MatProgressSpinnerDefaultOptions,
	MatProgressSpinnerModule
} from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MsalModule, MSAL_INSTANCE, MsalService, MsalRedirectComponent } from '@azure/msal-angular';
import { PublicClientApplication, IPublicClientApplication, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fass } from '@awesome.me/kit-bde9a98016/icons';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularDraggableModule } from 'angular2-draggable';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { QuillModule } from 'ngx-quill';
import { BehaviorSubject } from 'rxjs';

import { ApplicationModule } from '@nx-ahs/application';
import { BaseTranslateModule } from '@nx-ahs/base-translate';
import { provideFeatureFlag } from '@nx-ahs/feature-flags';
import { MailDialogService, MailService } from '@nx-ahs/mail';
import { TIMEOUT_DIALOG_COMPONENT, TimeoutComponent, TimeoutModule } from '@nx-ahs/timeout';
import { NoteListComponent } from '@nx-ahs/notes';
import { DynamicScriptingService } from '@nx-ahs/script-shared/services';
import { UserProfilePicComponent } from '@nx-ahs/shared/components';
import { PermissionIdDirective } from '@nx-ahs/shared/directives';
import { MomentFromNowPipe } from '@nx-ahs/shared/pipes';
import { FaMatIconRegistryService } from '@nx-ahs/shared/services';
import { SiteStorageModule } from '@nx-ahs/site-storage';
import { AhsTimerModule } from '@nx-ahs/ahs-timer';

import { environment } from '../environments/environment';
import { msalConfig } from '../msal/msal';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApplicationMenuComponent } from './core/components/application-menu/application-menu.component';
import { AppHttpInterceptor } from './core/interceptors/http-interceptor.service';
import { AccountService } from './core/services/account.service';
import { BrowserNotificationService } from './core/services/browser-notification.service';
import { ErrorHandlerService } from './core/services/error-handler.service';
import { HistoryService } from './core/services/history.service';
import { SecurityService } from './core/services/security.service';
import { UserSettingsService } from './core/services/user-settings.service';
import { BreadcrumbTrailComponent } from './modules/breadcrumbs/breadcrumb-trail/breadcrumb-trail.component';
import { ComplaintFormComponent } from './modules/complaint/complaint-form/complaint-form.component';
import { FinanceTextComponent } from './modules/finance/finance-text/finance-text.component';
import { LockinDateTextComponent } from './modules/finance/lockin-date-text/lockin-date-text.component';
import { FormlyCustomComponentsModule } from './modules/formly-custom-components/formly-custom-components.module';
import { InfoRequestToolComponent } from './modules/info-requests/info-request-tool/info-request-tool.component';

import { provideSharedDynamicPipes } from '@nx-ahs/shared/pipes';
import { NoteTranslationComponent } from './modules/translation/note-translation/note-translation.component';
import { EncounterActionsComponent } from './modules/tripetto-custom-components/encounter-actions/encounter-actions.component';
import { SiteStorageKeys } from './shared/constants/site-storage-keys';

const appearance: MatFormFieldDefaultOptions = {
	appearance: 'outline'
};

const tabs: MatTabsConfig = {
	animationDuration: '0ms'
};

const spinner: MatProgressSpinnerDefaultOptions = {
	color: 'primary'
};

export function loggerCallback(logLevel: LogLevel, message: string) {
	console.log(message);
}
export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: msalConfig.auth.clientId,
			authority: msalConfig.auth.authority,
			redirectUri: msalConfig.auth.redirectUri,
			postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: false
		},
		system: {
			allowPlatformBroker: false, // Disables WAM Broker
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Error,
				piiLoggingEnabled: false
			}
		}
	});
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		AngularDraggableModule,
		AppRoutingModule,
		BrowserModule,
		BrowserAnimationsModule,
		ApplicationMenuComponent,
		FontAwesomeModule,
		FormsModule,
		HttpClientModule,
		MatBadgeModule,
		MatButtonModule,
		MatDialogModule,
		MatInputModule,
		MatMenuModule,
		MatProgressBarModule,
		MatProgressSpinnerModule,
		MatRadioModule,
		MatSidenavModule,
		MatSnackBarModule,
		MatTableModule,
		MatToolbarModule,
		MsalModule,
		NoteListComponent,
		QuillModule.forRoot(),
		UserProfilePicComponent,
		PermissionIdDirective,
		MomentFromNowPipe,
		ReactiveFormsModule,
		ImageCropperComponent,
		SiteStorageModule.forRoot(),
		TranslateModule.forRoot(),
		FormlyModule.forRoot(),
		BaseTranslateModule.forRoot({
			languages: [
				{
					name: 'English',
					code: 'en',
					isDefault: true
				},
				{
					name: 'Spanish',
					code: 'es',
					isDefault: false
				},
				{
					name: 'Chinese',
					code: 'zh',
					isDefault: false
				},
				{
					name: 'Vietnamese',
					code: 'vi',
					isDefault: false
				},
				{
					name: 'Somali',
					code: 'so',
					isDefault: false
				}
			],
			siteLanguageStorageKey: SiteStorageKeys.siteLanguageKey
		}),
		AhsTimerModule,
		FlexLayoutModule,
		NgxExtendedPdfViewerModule,
		MatTooltipModule,
		ApplicationModule,
		BreadcrumbTrailComponent,
		FormlyCustomComponentsModule,
		TimeoutModule
	],
	providers: [
		{
			provide: MAT_TABS_CONFIG,
			useValue: tabs
		},
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: appearance
		},
		{
			provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
			useValue: { color: 'primary' }
		},
		{
			provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS,
			useValue: spinner
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AppHttpInterceptor,
			multi: true
		},
		{
			provide: ErrorHandler,
			useClass: ErrorHandlerService
		},
		{
			provide: 'environment',
			useValue: environment
		},
		{
			provide: 'clientConfig',
			useValue: new BehaviorSubject<any>({})
		},
		{
			provide: 'securityService',
			useFactory: (securityService: SecurityService) => {
				return securityService;
			},
			deps: [SecurityService]
		},
		{
			provide: 'historyService',
			useFactory: (historyService: HistoryService) => {
				return historyService;
			},
			deps: [HistoryService]
		},
		{
			provide: 'userSettingsService',
			useFactory: (userSettingsService: UserSettingsService) => {
				return userSettingsService;
			},
			deps: [UserSettingsService]
		},
		{
			provide: 'accountService',
			useFactory: (accountService: AccountService) => {
				return accountService;
			},
			deps: [AccountService]
		},
		{
			provide: 'mailService',
			useFactory: (mailService: MailService) => {
				return mailService;
			},
			deps: [MailService]
		},
		{
			provide: 'mailDialogService',
			useFactory: (mailDialogService: MailDialogService) => {
				return mailDialogService;
			},
			deps: [MailDialogService]
		},
		{
			provide: 'browserNotificationService',
			useFactory: (browserNotificationService: BrowserNotificationService) => {
				return browserNotificationService;
			},
			deps: [BrowserNotificationService]
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory
		},
		{
			provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
			useValue: { color: 'primary' }
		},
		MsalService,
		provideSharedDynamicPipes(),
		provideFeatureFlag(),
		{ provide: TIMEOUT_DIALOG_COMPONENT, useValue: TimeoutComponent }
	],

	bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
	constructor(
		private library: FaIconLibrary,
		private faMatIconRegistry: FaMatIconRegistryService,
		private dynamicScriptingService: DynamicScriptingService
	) {
		// Load custom FontAwesome kit icons and register them as mat-icons
		library.addIconPacks(fass);
		this.faMatIconRegistry.registerAll();

		//Add custom scripting components
		this.dynamicScriptingService.addCustomComponent('ComplaintFormComponent', ComplaintFormComponent);
		this.dynamicScriptingService.addCustomComponent('InfoRequestToolComponent', InfoRequestToolComponent);
		this.dynamicScriptingService.addCustomComponent('FinanceTextComponent', FinanceTextComponent);
		this.dynamicScriptingService.addCustomComponent('LockinDateTextComponent', LockinDateTextComponent);

		this.dynamicScriptingService.addCustomComponent('NoteTranslationComponent', NoteTranslationComponent);

		this.dynamicScriptingService.addCustomComponent('EncounterActionsComponent', EncounterActionsComponent);
	}
}
